/**
 * How-To page — fetches the README from the backend and renders it with
 * `marked`. Living docs: every time the repo README changes, this page
 * updates on the next backend reload with no frontend redeploy.
 */
import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link } from "react-router-dom";
import { marked } from "marked";
import axios from "axios";
import { Loader2, ArrowLeft, Book } from "lucide-react";

const API = `${BACKEND_URL}/api`;

// Sensible marked defaults: GFM, no auto-linking of raw URLs to avoid
// double-linked headings, respect line breaks inside tables.
marked.setOptions({ gfm: true, breaks: false, headerIds: true, mangle: false });

export default function HowToPage() {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/docs/readme`, {
          transformResponse: [(d) => d],  // keep as raw text, don't JSON.parse
        });
        if (cancel) return;
        setHtml(marked.parse(r.data));
      } catch (e) {
        if (!cancel) setError("Could not load the guide. Try again in a moment.");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="howto-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="howto-back-home">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center">
            <Book className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span> · GUIDE</div>
        </Link>
        <Link to="/" data-testid="howto-back-link"
              className="font-mono text-xs uppercase tracking-widest text-[#94A3B8] hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-3 h-3"/> Back home
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {loading && (
          <div className="flex items-center gap-2 text-[#94A3B8]" data-testid="howto-loading">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading guide…
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/5 p-4 text-sm text-[#F8FAFC]" data-testid="howto-error">
            {error}
          </div>
        )}
        {!loading && !error && (
          <article
            data-testid="howto-content"
            className="howto-body max-w-none"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </main>

      {/* Scoped typographic styles — we don't depend on @tailwindcss/typography
          so the guide renders correctly on a minimal install. */}
      <style>{`
        .howto-body { color: #E2E8F0; line-height: 1.6; }
        .howto-body h1 { font-size: 2.5rem; font-weight: 900; color: #FFFFFF; margin: 1.5rem 0 0.75rem; letter-spacing: -0.02em; }
        .howto-body h2 { font-size: 1.6rem; font-weight: 900; color: #FFFFFF; margin: 2.5rem 0 0.75rem; padding-bottom: 0.5rem; border-bottom: 1px solid #1E2638; }
        .howto-body h3 { font-size: 1.15rem; font-weight: 700; color: #00F0FF; margin: 1.5rem 0 0.5rem; }
        .howto-body p  { margin: 0.75rem 0; color: #CBD5E1; }
        .howto-body a  { color: #00F0FF; text-decoration: underline; text-underline-offset: 3px; }
        .howto-body a:hover { color: #33F3FF; }
        .howto-body strong { color: #FFFFFF; font-weight: 700; }
        .howto-body ul, .howto-body ol { padding-left: 1.5rem; margin: 0.75rem 0; }
        .howto-body li { margin: 0.35rem 0; color: #CBD5E1; }
        .howto-body code { background: #121620; color: #FFC107; padding: 0.15rem 0.4rem; border-radius: 4px; font-size: 0.85em; }
        .howto-body pre { background: #0B0E14; border: 1px solid #1E2638; padding: 1rem; border-radius: 8px; overflow-x: auto; margin: 1rem 0; }
        .howto-body pre code { background: transparent; color: #E2E8F0; padding: 0; }
        .howto-body hr { border: none; border-top: 1px solid #1E2638; margin: 2rem 0; }
        .howto-body blockquote { border-left: 3px solid #00F0FF; padding: 0.25rem 1rem; margin: 1rem 0; color: #94A3B8; background: #0B0E14; }
        .howto-body table { width: 100%; margin: 1rem 0; border-collapse: collapse; font-size: 0.9rem; }
        .howto-body th, .howto-body td { padding: 0.5rem 0.75rem; border: 1px solid #1E2638; text-align: left; }
        .howto-body th { background: #121620; color: #00F0FF; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; font-size: 0.75rem; }
        .howto-body td { color: #CBD5E1; }
      `}</style>
    </div>
  );
}
